import React, { useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CheckOutlined,
  DownloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { unparse } from "papaparse";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectIsKlearlySuperAdmin } from "../../access-control/accountSlice";
import {
  IIncidentObservedError,
  IncidentSummary,
} from "../../../interfaces/paymentHealth.interface";
import { downloadFile } from "../../../utils/downloadFile";
import { severityMeta, statusMeta } from "../paymentHealthDisplay";
import {
  markIncidentFalsePositiveThunk,
  resolveIncidentThunk,
  selectPaymentHealthActionIncidentId,
} from "../paymentHealthSlice";

interface IncidentCardProps {
  incident: IncidentSummary;
}

const IncidentCard: React.FC<IncidentCardProps> = ({ incident }) => {
  const dispatch = useAppDispatch();
  const isSuperAdmin = useAppSelector(selectIsKlearlySuperAdmin);
  const actionIncidentId = useAppSelector(selectPaymentHealthActionIncidentId);
  const isActing = actionIncidentId === incident.id;

  const sev = severityMeta(incident.severity);
  const status = statusMeta(incident.status);

  const errorColumns = [
    {
      title: "Error code",
      dataIndex: "errorCode",
      key: "errorCode",
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    { title: "Event", dataIndex: "event", key: "event" },
    {
      title: "Count",
      dataIndex: "eventCount",
      key: "eventCount",
      align: "right" as const,
    },
    {
      title: "Peak vs baseline",
      dataIndex: "peakRatioToBaseline",
      key: "peakRatioToBaseline",
      align: "right" as const,
      render: (v: number) => (
        <Tag color={v >= 5 ? "red" : v >= 2 ? "orange" : "gold"}>
          {`${(v ?? 0).toFixed(1)}x`}
        </Tag>
      ),
    },
    {
      title: "First seen",
      dataIndex: "firstSeen",
      key: "firstSeen",
      render: (v: string) => dayjs(v).format("MMM D, HH:mm"),
    },
    {
      title: "Last seen",
      dataIndex: "lastSeen",
      key: "lastSeen",
      render: (v: string) => dayjs(v).format("MMM D, HH:mm"),
    },
  ];

  function downloadCsv() {
    const rows = incident.observedErrors.map((e: IIncidentObservedError) => ({
      component: incident.component,
      errorCode: e.errorCode,
      event: e.event,
      eventCount: e.eventCount,
      peakRatioToBaseline: e.peakRatioToBaseline,
      firstSeen: e.firstSeen,
      lastSeen: e.lastSeen,
    }));
    const csv = unparse(rows);
    downloadFile(
      csv,
      `incident-${incident.component}-${incident.id}.csv`,
      "CSV"
    );
  }

  function confirmFalsePositive() {
    let note = "";
    Modal.confirm({
      title: "Mark incident as false positive?",
      icon: <StopOutlined style={{ color: "#faad14" }} />,
      content: (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            This dismisses the incident and blocks the alerter from re-opening
            it from the same signal for 60 minutes.
          </Typography.Text>
          <Input.TextArea
            placeholder="Optional note (why this is a false positive)"
            rows={3}
            onChange={(e) => {
              note = e.target.value;
            }}
          />
        </Space>
      ),
      okText: "Mark false positive",
      cancelText: "Cancel",
      onOk: () =>
        dispatch(
          markIncidentFalsePositiveThunk({
            incidentId: incident.id,
            note: note.trim() || undefined,
          })
        ).unwrap(),
    });
  }

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space wrap>
          <Typography.Text strong>{incident.component}</Typography.Text>
          <Tag color={sev.color}>{sev.label}</Tag>
          <Tag color={status.color}>{status.label}</Tag>
        </Space>
      }
      extra={
        <Space wrap>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={downloadCsv}
            disabled={incident.observedErrors.length === 0}
          >
            CSV
          </Button>
          {isSuperAdmin && (
            <>
              <Popconfirm
                title="Resolve this incident?"
                description="Marks it resolved and suppresses re-open for 60 minutes."
                okText="Resolve"
                cancelText="Cancel"
                onConfirm={() =>
                  dispatch(resolveIncidentThunk({ incidentId: incident.id }))
                }
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={isActing}
                >
                  Resolve
                </Button>
              </Popconfirm>
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                loading={isActing}
                onClick={confirmFalsePositive}
              >
                False positive
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
        <Descriptions.Item label="Started">
          {dayjs(incident.startedAt).format("MMM D, HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item label="Last observed">
          {dayjs(incident.lastObservedAt).format("MMM D, HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item label="Affected merchants">
          {incident.affectedMerchantCount}
        </Descriptions.Item>
        <Descriptions.Item label="Affected devices">
          {incident.affectedDeviceCount}
        </Descriptions.Item>
        <Descriptions.Item label="App versions" span={2}>
          {incident.affectedAppVersions.length
            ? incident.affectedAppVersions.map((v) => <Tag key={v}>{v}</Tag>)
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Text
        strong
        style={{ display: "block", marginTop: 16, marginBottom: 8 }}
      >
        Observed errors
      </Typography.Text>
      <Table
        size="small"
        rowKey={(r) => `${r.event}-${r.errorCode}`}
        columns={errorColumns}
        dataSource={incident.observedErrors}
        pagination={false}
        scroll={{ x: true }}
      />
    </Card>
  );
};

export default IncidentCard;
